import React, {useEffect, useMemo, useState} from 'react';
import {ActivityIndicator, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {
  defaultProfile,
  playableProfiles,
  type StreamCamera,
} from '@/entities/stream/types';
import {isConnecting, useAutoLive} from '../lib/use-auto-live';
import {VideoSurface} from './VideoSurface';
import {LiveStats} from './LiveStats';
import {Banner} from '@/shared/ui/banner';
import {Card} from '@/shared/ui/card';
import {ChoiceChips} from '@/shared/ui/segmented';
import {StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * One camera, playable.
 *
 * The picture starts on mount and there is no play button — the owner wants it to
 * behave like an NVR (2026-10-12). What keeps a wall of these off the site's uplink is
 * the profile: each opens on the SMALLEST playable one, and leaving the screen
 * unmounts the player, which closes the session.
 *
 * ## Quality
 *
 * **Profile** is quality, and it opens on the SMALLEST playable one for the reason
 * above. H265 profiles are not offered at all — a browser cannot decode them, and what
 * it shows instead is a black rectangle with no error, which reads as a broken camera.
 * A camera with nothing but H265 says so in words.
 *
 * The «Только через TURN» switch was removed at the owner's request (2026-10-12). Relay
 * is still used whenever direct fails; the route tile in `LiveStats` shows which path
 * a session took. Testing relay on purpose now means `relayOnly` in `useLiveStream`.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  stage: {
    borderRadius: radii.lg,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  waiting: {alignItems: 'center', gap: spacing(3), padding: spacing(4)},
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    flexWrap: 'wrap',
  },
  spacer: {flex: 1},
  controls: {gap: spacing(3)},
});

export function CameraPlayer({
  companyId,
  camera,
  compact = false,
  initialShape = 16 / 9,
}: {
  companyId: string;
  camera: StreamCamera;
  /** A tile on the camera wall: picture and route only — no quality, TURN, stats. */
  compact?: boolean;
  /** Width/height to draw before the first frame says otherwise (9/16 for a terminal). */
  initialShape?: number;
}) {
  const {t} = useTranslation();
  const p = useTheme();

  const playable = useMemo(() => playableProfiles(camera), [camera]);
  const [profile, setProfile] = useState(() => defaultProfile(camera)?.token ?? '');

  // Starts by itself, restarts when the quality changes and retries a failure with a
  // back-off — see `useAutoLive`.
  const {state} = useAutoLive({companyId, camera, profile: profile || null});

  // The frame takes the picture's own shape. A FaceID terminal sends portrait 360×640
  // and a 16:9 box pillarboxed it into a strip (2026-10-12). The shape is remembered
  // after the stream stops, so the box does not jump back while reconnecting. Portrait
  // is capped by height and centred — full width would make it taller than the screen.
  const [shape, setShape] = useState(initialShape);
  const frameW = state.video?.width;
  const frameH = state.video?.height;
  useEffect(() => {
    if (frameW && frameH) {
      setShape(frameW / frameH);
    }
  }, [frameW, frameH]);
  // A wall tile is already sized for its shape: fill it. Only the full player, which
  // spans the whole row, caps a portrait picture by height.
  const stageShape =
    shape >= 1 || compact
      ? {aspectRatio: shape}
      : {
          aspectRatio: shape,
          height: compact ? 420 : 640,
          maxWidth: '100%' as const,
          alignSelf: 'center' as const,
        };

  const live = state.phase === 'playing' && state.stream;
  const busy = isConnecting(state.phase);

  if (playable.length === 0) {
    return (
      <Banner
        tone="warning"
        title={t('live.noPlayableProfile')}
        message={t('live.noPlayableProfileHint')}
      />
    );
  }

  // `available: false` means the source stopped reporting this camera. The row is kept
  // on purpose so its disappearance is visible; calling into it would answer 480.
  if (!camera.available) {
    return (
      <Banner
        tone="warning"
        title={t('live.unavailable')}
        message={t('live.unavailableHint')}
      />
    );
  }

  return (
    <View style={styles.root}>
      <View style={[styles.stage, stageShape, {backgroundColor: p.foreground}]}>
        {live ? (
          <VideoSurface stream={state.stream!} />
        ) : (
          <View style={styles.waiting}>
            {busy ? <ActivityIndicator color={p.background} /> : null}
            <AppText variant="caption" color={p.background}>
              {busy
                ? t(`live.phase.${state.phase}`)
                : state.phase === 'error' && state.errorKey
                  ? // The stage said «Запрашиваем доступ…» over a 480 — the banner below
                    // and the picture disagreed. Say what actually happened.
                    t(state.errorKey)
                  : t('live.phase.token')}
            </AppText>
          </View>
        )}
      </View>

      {state.phase === 'error' && state.errorKey ? (
        <Banner
          tone="error"
          title={t(state.errorKey)}
          // The numeric code is not decoration: 403 and 480 read alike to a user and
          // mean opposite things, and this is what turns a support call into a
          // one-line answer.
          message={state.errorCode ? t('live.code', {code: state.errorCode}) : undefined}
        />
      ) : null}

      <View style={styles.bar}>
        <View style={styles.spacer} />
        {state.route ? (
          <StatusPill
            tone={state.route === 'relay' ? 'pending' : 'online'}
            size="sm"
            dot={false}
            label={t(`live.route.${state.route}`)}
          />
        ) : null}
      </View>

      {compact ? null : <LiveStats state={state} />}

      {compact ? null : (
        <Card padding={4} style={styles.controls}>
          <AppText variant="label">{t('live.quality')}</AppText>
          <ChoiceChips
            options={playable.map(pr => ({
              value: pr.token,
              label: `${pr.name} · ${pr.width}×${pr.height}`,
            }))}
            value={profile}
            // The profile is baked into `connect`, so a change is a new session — the
            // effect above redials, and `start` closes the old one first.
            onChange={setProfile}
            testID="live-profile"
          />
        </Card>
      )}
    </View>
  );
}
